<template>
    <BeneficiaryModalWrapper>
        <ModalOverlay @click.stop>
            <BeneficiaryModalContent @click.stop>
                <BeneficiaryModalHeader>
                    <ModalTitle>고객 선택</ModalTitle>
                    <CloseButton @click="$emit('close')">&times;</CloseButton>
                </BeneficiaryModalHeader>

                <BeneficiaryModalBody>
                    <!-- 검색 섹션 -->
                    <SearchSection>
                        <SearchRow>
                            <SearchGroup>
                                <SearchLabel>고객 검색</SearchLabel>
                                <SearchInput type="text" v-model="searchQuery" placeholder="고객명 또는 인정번호를 입력하세요"
                                    @keyup.enter="searchHandler" />
                                <SearchButton @click="searchHandler">
                                    <Magnify :size="18" />
                                </SearchButton>
                            </SearchGroup>
                        </SearchRow>

                        <SearchRow>
                            <FilterGroup>
                                <SearchLabel>대상 필터</SearchLabel>
                                <FilterChips>
                                    <FilterChip v-for="type in benefitTypes" :key="type.value"
                                        :active="selectedBenefitType === type.value"
                                        @click="filterByBenefitType(type.value)">
                                        {{ type.label }}
                                    </FilterChip>
                                </FilterChips>
                            </FilterGroup>
                        </SearchRow>
                    </SearchSection>

                    <!-- 고객 테이블 -->
                    <BeneficiaryTableContainer>
                        <BeneficiaryTable>
                            <thead>
                                <tr>
                                    <th>고객명</th>
                                    <th>인정번호</th>
                                    <th>생년월일</th>
                                    <th>대상</th>
                                    <th>고객 구분</th>
                                    <th>연락처</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="client in paginatedClients" :key="client.number" :class="{
                                    'selected': selectedClient && selectedClient.number === client.number,
                                    'blurred': isTestUser
                                }" @click="selectClient(client)">
                                    <td>
                                        <ClientName>{{ client.name }}</ClientName>
                                    </td>
                                    <td>{{ client.number }}</td>
                                    <td>{{ client.regident || '-' }}</td>
                                    <td>
                                        <BenefitTypeChip :type="client.target">
                                            {{ client.target }}
                                        </BenefitTypeChip>
                                    </td>
                                    <td>
                                        <BenefitTypeChip :type="client.customer_type">
                                            {{ client.customer_type }}
                                        </BenefitTypeChip>
                                    </td>
                                    <td>
                                        <ContactInfo>
                                            <div>{{ client.phone1 || '-' }}</div>
                                            <div v-if="client.phone2 && client.phone2 !== client.phone1">
                                                {{ client.phone2 }}
                                            </div>
                                        </ContactInfo>
                                    </td>
                                </tr>
                            </tbody>
                        </BeneficiaryTable>

                        <!-- 데이터 없음 표시 -->
                        <EmptyState v-if="clients_info.clients.length === 0">
                            <EmptyIcon>👥</EmptyIcon>
                            <EmptyMessage>고객이 없습니다</EmptyMessage>
                            <EmptySubMessage v-if="searchQuery || selectedBenefitType !== 'all'">
                                검색 조건을 변경하거나 필터를 초기화해보세요.
                            </EmptySubMessage>
                            <EmptySubMessage v-else>
                                등록된 고객이 없습니다.
                            </EmptySubMessage>
                        </EmptyState>
                    </BeneficiaryTableContainer>

                    <!-- 페이지네이션 -->
                    <PaginationContainer v-if="totalPages > 1">
                        <Pagination>
                            <PaginationButton :disabled="currentPage === 1" @click="goToPage(1)">
                                <ChevronDoubleLeft :size="15" />
                            </PaginationButton>
                            <PaginationButton :disabled="currentPage <= 10" @click="goToPageGroup('prev')">
                                <ChevronLeft :size="15" />
                            </PaginationButton>

                            <PaginationNumber v-for="page in displayedPages" :key="page" :active="page === currentPage"
                                @click="goToPage(page)">
                                {{ page }}
                            </PaginationNumber>

                            <PaginationButton :disabled="currentPage > Math.floor(totalPages / 10) * 10"
                                @click="goToPageGroup('next')">
                                <ChevronRight :size="20" />
                            </PaginationButton>
                            <PaginationButton :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
                                <ChevronDoubleRight :size="20" />
                            </PaginationButton>
                        </Pagination>
                    </PaginationContainer>
                </BeneficiaryModalBody>

                <BeneficiaryModalFooter>
                    <CancelButton @click="$emit('close')">취소</CancelButton>
                    <SaveButton @click="handleSave" :disabled="!selectedClient || isSaving">
                        {{ isSaving ? '등록 중...' : '등록' }}
                    </SaveButton>
                </BeneficiaryModalFooter>
            </BeneficiaryModalContent>
        </ModalOverlay>
    </BeneficiaryModalWrapper>
</template>

<script>
import * as BeneficiaryModalCss from '@/assets/styles/manage/modal/BeneficiaryModalCss.js';

import ChevronDoubleLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ChevronDoubleRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';

export default {
    name: 'BeneficiaryModal',

    components: {
        ...BeneficiaryModalCss,
        ChevronDoubleLeft,
        ChevronDoubleRight,
        ChevronLeft,
        ChevronRight,
        Magnify,
    },

    emits: ['close', 'save'],

    data() {
        return {
            // 로딩 상태
            isLoading: false,
            isSaving: false,

            // 고객 데이터
            clients_info: {
                clients: [],
                total_all_cnt: 0,
                general_cnt: 0,
                basic_cnt: 0,
                discount_cnt: 0,
                medical_cnt: 0,
                non_benefit_cnt: 0
            },
            clients: [],
            totalCount: 0,
            selectedClient: null,

            // 검색 및 필터링
            searchQuery: '',
            searchSubmitted: '',
            selectedBenefitType: 'all',

            // 페이징
            currentPage: 1,
            itemsPerPage: 10,

            // 필터 옵션
            benefitTypes: [
                { value: 'all', label: '전체' },
                { value: '일반', label: '일반' },
                { value: '기초', label: '기초' },
                { value: '감경', label: '감경' },
                { value: '의료급여', label: '의료급여' },
                { value: '산소', label: '산소' },
                { value: '당뇨', label: '당뇨' },
                { value: '보장구', label: '보장구' }
            ],

            escapeHandler: null
        }
    },

    computed: {
        paginatedClients() {
            return this.clients_info.clients;
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.clients_info.total_all_cnt / this.itemsPerPage));
        },

        displayedPages() {
            const currentGroup = Math.floor((this.currentPage - 1) / 10);
            const startPage = currentGroup * 10 + 1;
            const endPage = Math.min(startPage + 9, this.totalPages);

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            return pages;
        },
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
    },

    methods: {
        async fetchClients() {
            if (this.isLoading) return;
            this.isLoading = true;

            try {
                // 전역 상태에서 id가 없는 경우 처리
                if (!this.$globalState.company_key) {
                    const userInfo = localStorage.getItem('userInfo');
                    if (userInfo) {
                        const parsedInfo = JSON.parse(userInfo);
                        this.$setGlobalState('id', parsedInfo.id);
                        this.$setGlobalState('manager_name', parsedInfo.name);
                        this.$setGlobalState('company_name', parsedInfo.company);
                        this.$setGlobalState('company_key', parsedInfo.company_key);
                    } else {
                        this.$swal.fire('인증 오류', '로그인이 필요합니다.', 'error');
                        this.$router.push('/login');
                        return;
                    }
                }

                if (!this.$globalState.company_key) {
                    this.$swal.fire('인증 오류', '사업소 정보를 찾을 수 없습니다.', 'error');
                    this.$router.push('/login');
                    return;
                }

                this.searchSubmitted = this.searchQuery;

                // 백엔드 API를 통해 수급자 정보 조회 (급여 + 비급여)
                const apiUrl = `${this.$apiUrls.hanqmedi}/getClients/${this.$globalState.company_key}`;

                const response = await this.$axios({
                    method: 'get',
                    url: apiUrl,
                    params: {
                        page: this.currentPage,
                        limit: this.itemsPerPage,
                        search: this.searchSubmitted,
                        target: this.selectedBenefitType !== 'all' ? this.selectedBenefitType : '',
                        type: this.selectedTypeFilter !== '전체' ? this.selectedTypeFilter : '',
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                // API 응답 구조에 맞게 수정
                if (response.data && response.data.recipients && Array.isArray(response.data.recipients)) {
                    // API 응답 데이터를 프론트엔드에서 사용하는 형태로 매핑
                    this.clients_info.clients = response.data.recipients.map(client => ({
                        name: client.name || '',
                        number: client.number || '',
                        cnum: client.cnum || '',
                        target: client.target || '',
                        type: client.type || '급여',
                        customer_type: client.customer_type || '일반',
                        address: client.address || '',
                        address_detail: client.address_detail || '',
                        address2: client.address2 || '',
                        address_detail2: client.address_detail2 || '',
                        guardname: client.guard_name || '',
                        guardtarget: client.guard_target || '',
                        manager: client.manager || '',
                        phone1: client.phone1 || '',
                        phone2: client.phone2 || '',
                        // 추가 필드들도 매핑
                        id: client.id,
                        company_id: client.company_id,
                        user_id: client.user_id,
                        address2: client.address2 || '',
                        address_detail: client.address_detail || client.address2 || '',
                        address_num: client.address_num || '',
                        bank_accout: client.bank_accout || '',
                        bank_admin: client.bank_admin || '',
                        bank_name: client.bank_name || '',
                        bank_phone: client.bank_phone || '',
                        bank_resident: client.bank_resident || '',
                        center_name: client.center_name || '',
                        cnum: client.cnum || '',
                        gender: client.gender || '',
                        guard_resident: client.guard_resident || '',
                        memo: client.memo || '',
                        msgno: client.msgno || '',
                        ranker: client.ranker || '',
                        rcgt: client.rcgt || '',
                        sale: client.sale || 0,
                        created_at: client.created_at || '',
                        apdt_s: client.apdt_s || '',
                        apdt_e: client.apdt_e || '',
                        chk: client.chk || '',
                        // 출처 구분 정보 추가
                        source: client.source || 'unknown',
                        isNonBenefit: client.isNonBenefit || false
                    }));

                    // total_count로 총 개수 가져오기
                    this.clients_info.total_all_cnt = response.data.total_count || 0;

                    // 통계 계산 (target 필드 기반)
                    const stats = {
                        general_cnt: 0,
                        basic_cnt: 0,
                        discount_cnt: 0,
                        medical_cnt: 0,
                        non_benefit_cnt: 0,
                        // 비급여 고객 customer_type별 통계
                        non_benefit_general_cnt: 0,
                        non_benefit_oxygen_cnt: 0,
                        non_benefit_diabetes_cnt: 0,
                        non_benefit_assistive_device_cnt: 0
                    };

                    response.data.recipients.forEach(client => {
                        if (client.isNonBenefit || client.type === '비급여') {
                            // 비급여 고객 - customer_type별 카운팅
                            stats.non_benefit_cnt++;
                            switch (client.customer_type) {
                                case '일반':
                                    stats.non_benefit_general_cnt++;
                                    break;
                                case '산소': // 두 가지 값 모두 처리
                                    stats.non_benefit_oxygen_cnt++;
                                    break;
                                case '당뇨':
                                    stats.non_benefit_diabetes_cnt++;
                                    break;
                                case '보장구':
                                    stats.non_benefit_assistive_device_cnt++;
                                    break;
                                default:
                                    // customer_type이 없는 경우 기본값으로 '일반' 처리
                                    stats.non_benefit_general_cnt++;
                                    break;
                            }
                        } else {
                            // 급여 고객 - target별 카운팅
                            switch (client.target) {
                                case '일반':
                                    stats.general_cnt++;
                                    break;
                                case '기초':
                                    stats.basic_cnt++;
                                    break;
                                case '감경':
                                    stats.discount_cnt++;
                                    break;
                                case '의료급여':
                                    stats.medical_cnt++;
                                    break;
                            }
                        }
                    });

                    this.clients_info.general_cnt = stats.general_cnt;
                    this.clients_info.basic_cnt = stats.basic_cnt;
                    this.clients_info.discount_cnt = stats.discount_cnt;
                    this.clients_info.medical_cnt = stats.medical_cnt;
                    this.clients_info.non_benefit_cnt = stats.non_benefit_cnt;
                    this.clients_info.non_benefit_general_cnt = stats.non_benefit_general_cnt;
                    this.clients_info.non_benefit_oxygen_cnt = stats.non_benefit_oxygen_cnt;
                    this.clients_info.non_benefit_diabetes_cnt = stats.non_benefit_diabetes_cnt;
                    this.clients_info.non_benefit_assistive_device_cnt = stats.non_benefit_assistive_device_cnt;

                    // 필터링된 검색 결과가 없을 때 알림
                    if ((this.searchSubmitted || this.selectedBenefitType !== 'all') && this.clients_info.total_all_cnt === 0) {
                        let conditions = [];
                        if (this.searchSubmitted) conditions.push(`검색어: "${this.searchSubmitted}"`);
                        if (this.selectedBenefitType !== 'all') conditions.push(`대상: ${this.getBenefitTypeLabel(this.selectedBenefitType)}`);

                        this.$swal.fire({
                            title: '검색 결과 없음',
                            text: `${conditions.join(', ')} 조건에 맞는 고객이 없습니다.`,
                            icon: 'info',
                            confirmButtonText: '확인',
                            confirmButtonColor: '#0f4c5c',
                            showCloseButton: true,
                            timer: 3000,
                            timerProgressBar: true
                        });
                    }
                } else {
                    this.clients_info.clients = [];
                    this.clients_info.total_all_cnt = 0;
                    this.clients_info.general_cnt = 0;
                    this.clients_info.basic_cnt = 0;
                    this.clients_info.discount_cnt = 0;
                    this.clients_info.medical_cnt = 0;
                    this.clients_info.non_benefit_cnt = 0;
                    this.clients_info.non_benefit_general_cnt = 0;
                    this.clients_info.non_benefit_oxygen_cnt = 0;
                    this.clients_info.non_benefit_diabetes_cnt = 0;
                    this.clients_info.non_benefit_assistive_device_cnt = 0;
                }
            } catch (error) {
                console.error('데이터를 가져오는 중 오류 발생:', error);
                this.clients_info.clients = [];
                this.clients_info.total_all_cnt = 0;
                this.clients_info.general_cnt = 0;
                this.clients_info.basic_cnt = 0;
                this.clients_info.discount_cnt = 0;
                this.clients_info.medical_cnt = 0;
                this.clients_info.non_benefit_cnt = 0;
                this.clients_info.non_benefit_general_cnt = 0;
                this.clients_info.non_benefit_oxygen_cnt = 0;
                this.clients_info.non_benefit_diabetes_cnt = 0;
                this.clients_info.non_benefit_assistive_device_cnt = 0;
            } finally {
                this.isLoading = false;
            }
        },

        // 검색 및 필터링
        searchHandler() {
            this.currentPage = 1;
            this.selectedClient = null;
            this.fetchClients();
        },

        filterByBenefitType(type) {
            this.selectedBenefitType = type;
            this.currentPage = 1;
            this.selectedClient = null;
            this.fetchClients();
        },

        // 고객 선택
        selectClient(client) {
            this.selectedClient = client;
        },

        // 페이지 이동
        async goToPage(page) {
            if (page < 1 || page > this.totalPages || page === this.currentPage) {
                return;
            }
            this.currentPage = page;
            await this.fetchClients();
        },

        // 페이지 그룹 이동
        async goToPageGroup(direction) {
            let newPage;
            const currentGroup = Math.floor((this.currentPage - 1) / 10);

            if (direction === 'prev') {
                newPage = Math.max(1, (currentGroup - 1) * 10 + 1);
            } else {
                newPage = Math.min(this.totalPages, (currentGroup + 1) * 10 + 1);
            }

            if (newPage !== this.currentPage) {
                this.currentPage = newPage;
                await this.fetchClients();
            }
        },

        // 페이지당 항목 수 변경
        changeItemsPerPage() {
            this.currentPage = 1;
            this.fetchClients();
        },

        async handleSave() {
            if (!this.selectedClient) {
                this.$swal.fire('알림', '고객을 선택해주세요.', 'warning');
                return;
            }

            this.isSaving = true;

            try {
                // phone1만 사용하고 phone으로 전달
                const phone = this.selectedClient.phone1 || null;

                // 백엔드 API 호출하여 b_document 테이블에 고객 등록
                const response = await this.$axios({
                    method: 'post',
                    url: `${this.$apiUrls.hanqmedi}/registerBeneficiaryToDocument`,
                    data: {
                        beneficiary_name: this.selectedClient.name,
                        beneficiary_phone: phone,
                        companykey: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.success) {
                    this.$swal.fire({
                        title: '성공',
                        text: `${this.selectedClient.name}님이 고객 목록에 등록되었습니다.`,
                        icon: 'success'
                    });

                    // 부모 컴포넌트로 성공 결과 전달
                    this.$emit('save', {
                        ...this.selectedClient,
                        document_id: response.data.data?.id,
                        phone: phone
                    });

                } else {
                    throw new Error(response.data?.message || '등록에 실패했습니다.');
                }

            } catch (error) {
                console.error('고객 등록 실패:', error);

                // 중복 등록 에러 처리
                if (error.response?.data?.message?.includes('이미 등록')) {
                    this.$swal.fire({
                        title: '이미 등록됨',
                        text: `${this.selectedClient.name}님은 이미 문서 관리 대상자로 등록되어 있습니다.`,
                        icon: 'warning'
                    });
                } else {
                    this.$swal.fire({
                        title: '등록 실패',
                        text: error.response?.data?.message || '고객 등록 중 오류가 발생했습니다.',
                        icon: 'error'
                    });
                }
            } finally {
                this.isSaving = false;
            }
        },

        handleEscape(e) {
            if (e.key === 'Escape') {
                this.$emit('close');
            }
        }
    },

    async mounted() {
        // ESC 키로 모달 닫기
        this.escapeHandler = this.handleEscape;
        document.addEventListener('keydown', this.escapeHandler);

        // 고객 목록 로드
        await this.fetchClients();
    },

    unmounted() {
        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        if (this.escapeHandler) {
            document.removeEventListener('keydown', this.escapeHandler);
        }
    }
}
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>